import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useMemo } from "react";
import { Formik, ErrorMessage } from "formik";
import * as Yup from "yup";
import { REQUIRED_MESSAGE } from "@/utils/form";
import { useNotificationStore } from "@/store/notification";
import { useToast } from "@/components/ui/use-toast";
import { TextAreaField } from "@/components/forms/fields";
import { PayIcon } from "@/components/Icons/PayIcon";
import { Loader } from "../Loader/Loader";
import { useUserStore } from "@/store/user";

export type SupportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const SupportModal: React.FC<SupportModalProps> = ({ isOpen, onClose }) => {
  const submitSupportRequest = useUserStore((state) => state.submitSupportRequest);
  const initialValues = useMemo(() => {
    return {
      message: ""
    } as unknown as SupportFormType;
  }, []);
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();

  return (
    <Dialog open={isOpen} modal defaultOpen={isOpen}>
      <DialogContent
        className={cn(
          "custom-scrollbar max-h-[100vh] w-full gap-0 overflow-y-auto bg-gray-dark-1400 px-0 py-0 !pr-0 lg:min-w-[560px]"
        )}
        showCloseIcon={false}
      >
        <DialogHeader className="gap-2 p-5">
          <DialogTitle className="text-2xl">Need help?</DialogTitle>
          <div className="text-base text-gray-dark-1000">Submit a support ticket below.</div>
        </DialogHeader>

        <Formik<SupportFormType>
          initialValues={initialValues}
          onSubmit={async (values) => {
            try {
              await submitSupportRequest(values.message);

              toast({
                title: "We've received your request — hang tight, we'll follow up via email.",
                variant: "success"
              });

              onClose();
            } catch (e) {
              if (e instanceof Error) {
                displayError({
                  title: "Something went wrong",
                  message: "Please refresh and resubmit."
                });
              }
            }
          }}
          validationSchema={SupportFormSchema}
          validateOnMount={true}
          validateOnChange={true}
        >
          {({ handleSubmit, touched, errors, isValid, isSubmitting }) => {
            return (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <PayIcon className="invisible absolute left-0 top-0" />
                <div className="flex flex-col gap-5 border-y border-gray-dark-200 p-5 pb-0">
                  <div className="flex flex-col gap-2">
                    <Label className="text-lg">
                      Message <span className="text-blue-light-300">*</span>
                    </Label>
                    <TextAreaField
                      name="message"
                      placeholder="Tell us what's going on. The more details, the better!"
                      className={cn(
                        "bg-gray-dark-600 text-foreground placeholder:text-gray-light-1700 focus:border-gray-dark-200 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                        errors.message && touched.message
                          ? "border-red-dark-300"
                          : "border-gray-light-1900"
                      )}
                    />
                    <ErrorMessage
                      name={`message`}
                      component="div"
                      className="subtext-x-small -mt-5 text-red-light-200"
                    />
                  </div>
                </div>

                <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg px-4 py-4 dark:border-gray-dark-200 dark:bg-black">
                  <div className="flex-1"></div>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    data-testid="ConfirmModal_Button"
                  >
                    Cancel
                  </Button>

                  <Button
                    size="sm"
                    className="gap-2"
                    type="submit"
                    disabled={!isValid || isSubmitting}
                  >
                    <span>Submit</span>
                    {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
                  </Button>
                </DialogFooter>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

type SupportFormType = {
  message: string;
};

const SupportFormSchema = Yup.object().shape({
  message: Yup.string().required(REQUIRED_MESSAGE)
});
